import React from 'react';
import { useSelector } from 'react-redux';
import { getPostCategoryInThai } from '../../../common/helpers/domain/postCategories';
import {
  isVideoPost,
  VideoType,
} from '../../../common/helpers/domain/videoTypes';
import { getContentPagePath } from '../../../common/helpers/routing/urlGenerator';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import commonResource from '../../../locales/th/common.json';
import { HeroArticleInfo } from '../../../queries/homePage/heroArticles';
import { WholeState } from '../../../store/common/type';
import { PlayIconSvg } from '../../heroVideo/PlayIconSvg';
import { heroArticleImageConfig } from './config';
import {
  Author,
  AuthorName,
  HeroArticleCaption,
  HeroArticleCategory,
  HeroArticleImage,
  HeroArticleMeta,
  HeroArticleTitle,
  HeroArticleWrapper,
  ImageOverlay,
} from './HeroArticle.style';

interface HeroArticleProps {
  heroArticleData: HeroArticleInfo;
  onClickHeroArticle?: (heroArticleData: HeroArticleInfo) => void;
}

const HeroArticle: React.FC<HeroArticleProps> = ({
  heroArticleData,
  onClickHeroArticle,
}) => {
  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );
  const imageInfo = heroArticleImageConfig[deviceType];
  const isNextGenSupported = useSelector<WholeState, boolean>((state) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );

  if (!heroArticleData) {
    return <HeroArticleWrapper data-test={'hero-article'} />;
  }

  const { title, author, postCategory, link, videoType, featuredImage } =
    heroArticleData;

  const articleLink = getContentPagePath({ link });
  const sharpieImgUri = handleImageUrl(
    featuredImage?.url,
    imageInfo.width,
    imageInfo.height,
    isNextGenSupported,
  );

  return (
    <HeroArticleWrapper
      data-test={'hero-article'}
      onClick={() => onClickHeroArticle(heroArticleData)}
    >
      <HeroArticleImage>
        <a data-test="hero-article-image-link" href={articleLink}>
          <ImageOverlay />
          {isVideoPost(videoType as VideoType) && (
            <span className="play-icon-wrapper">
              <PlayIconSvg />
            </span>
          )}
          <img src={sharpieImgUri} alt={title} />
        </a>
        <HeroArticleCaption>
          <a data-test="hero-article-caption-link" href={articleLink}>
            <HeroArticleCategory>
              {getPostCategoryInThai(postCategory)}
            </HeroArticleCategory>
            <HeroArticleTitle id="hero-article-title">{title}</HeroArticleTitle>
          </a>
          <HeroArticleMeta>
            {author?.name && (
              <Author>
                <AuthorName data-test="hero-article-author-link">
                  <span>{commonResource.by + ' '}</span>
                  {author.name}
                </AuthorName>
              </Author>
            )}
          </HeroArticleMeta>
        </HeroArticleCaption>
      </HeroArticleImage>
    </HeroArticleWrapper>
  );
};

export default HeroArticle;
